import React, { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { ArrowLeftIcon } from '@heroicons/react/24/outline';
import { Form } from '../../shared/FormComponents';
import BasicInfoSection from './sections/BasicInfoSection';
import PaymentDetailsSection from './sections/PaymentDetailsSection';
import TaxPlanningSection from './sections/TaxPlanningSection';
import useIncome from '../../../hooks/useIncome';
import { calculateTaxSummary, formatTaxSummary } from '../../shared/taxCalculator';

const ContractorForm = ({ bucketId, income = null, onSuccess, onBack, isEditing }) => {
  const { createIncome, updateIncome } = useIncome();
  // Use the provided isEditing prop or determine from income existence
  isEditing = isEditing || !!income;

  // Initialize form with existing data if editing
  const defaultValues = isEditing ? {
    name: income.name || '',
    employer: income.employer || income.employerName || 
             (income.typeSpecificDetails?.contractor?.employer) || '',
    startDate: income.startDate ? new Date(income.startDate).toISOString().split('T')[0] : '',
    state: (income.location && income.location.state) || 
           (income.typeSpecificDetails?.contractor?.state) || '',
    grossPay: income.grossAmount || income.amount || '',
    frequency: (income.frequency || 'MONTHLY').toUpperCase(),
    netPay: income.netAmount || income.net || '',
    isActive: !income.endDate,
    endDate: income.endDate ? new Date(income.endDate).toISOString().split('T')[0] : '',
    businessExpenses: income.typeSpecificDetails?.contractor?.expenses?.totalBusinessExpenses || '',
    autoWithholdTaxes: false,
    filingStatus: income.typeSpecificDetails?.contractor?.filingStatus || '',
    businessStructure: income.typeSpecificDetails?.contractor?.businessStructure || '',
    // Business expenses
    homeOfficeSqft: income.typeSpecificDetails?.contractor?.expenses?.homeOfficeSqft || '',
    totalHomeSqft: income.typeSpecificDetails?.contractor?.expenses?.totalHomeSqft || '',
    monthlyHomeExpenses: income.typeSpecificDetails?.contractor?.expenses?.monthlyHomeExpenses || '',
    equipmentExpenses: income.typeSpecificDetails?.contractor?.expenses?.equipmentExpenses || '',
    professionalServices: income.typeSpecificDetails?.contractor?.expenses?.professionalServices || '',
    travelExpenses: income.typeSpecificDetails?.contractor?.expenses?.travelExpenses || '',
    marketingExpenses: income.typeSpecificDetails?.contractor?.expenses?.marketingExpenses || '',
    healthInsurance: income.typeSpecificDetails?.contractor?.expenses?.healthInsurance || '',
    otherExpenses: income.typeSpecificDetails?.contractor?.expenses?.otherExpenses || '',
    retirementContribution: income.typeSpecificDetails?.contractor?.expenses?.retirementContribution || '',
    hsaContribution: income.typeSpecificDetails?.contractor?.expenses?.hsaContribution || ''
  } : {
    name: '',
    employer: '',
    startDate: '',
    state: '',
    grossPay: '',
    frequency: '',
    netPay: '',
    isActive: true,
    endDate: '',
    businessExpenses: '',
    autoWithholdTaxes: false,
    filingStatus: '',
    homeOfficeSqft: '',
    totalHomeSqft: '',
    monthlyHomeExpenses: '',
    equipmentExpenses: '',
    professionalServices: '',
    travelExpenses: '',
    marketingExpenses: '',
    healthInsurance: '',
    otherExpenses: '',
    retirementContribution: '',
    hsaContribution: '',
    businessStructure: ''
  };

  const methods = useForm({
    defaultValues,
    mode: 'onChange'
  });

  const { register, handleSubmit, watch, setValue, formState: { errors, isSubmitting } } = methods;

  // Watch form fields for calculations
  const grossPay = watch('grossPay');
  const frequency = watch('frequency');
  const state = watch('state');
  const businessExpenses = watch([
    'homeOfficeSqft',
    'totalHomeSqft',
    'monthlyHomeExpenses',
    'equipmentExpenses',
    'professionalServices',
    'travelExpenses',
    'marketingExpenses',
    'healthInsurance',
    'otherExpenses',
    'retirementContribution',
    'hsaContribution'
  ]);

  // Calculate net pay and update form when gross pay or expenses change
  useEffect(() => {
    console.log('💰 ContractorForm - useEffect triggered:', { grossPay, frequency, state });
    
    if (grossPay && frequency && state) {
      // Calculate business expenses - need to convert to monthly for tax calculation
      const convertToMonthly = (amount) => {
        const numAmount = parseFloat(amount) || 0;
        switch (frequency.toLowerCase()) {
          case 'weekly':
            return numAmount * 52 / 12;
          case 'biweekly':
            return numAmount * 26 / 12;
          case 'monthly':
            return numAmount;
          case 'quarterly':
            return numAmount / 3;
          case 'yearly':
            return numAmount / 12;
          default:
            return numAmount;
        }
      };

      // Calculate total monthly business expenses
      const monthlyExpenses = Object.values(businessExpenses).reduce((sum, val) => {
        return sum + convertToMonthly(val);
      }, 0);

      console.log('📊 Business expenses calculation:', { 
        businessExpenses, 
        monthlyExpenses,
        frequency 
      });

      // Calculate tax summary (expects annual deductions)
      const taxSummary = calculateTaxSummary({
        grossPay: parseFloat(grossPay),
        frequency,
        state,
        filingStatus: watch('filingStatus') || 'single', // Provide default for calculation
        dependents: 0,
        otherIncome: 0,
        deductions: monthlyExpenses * 12, // Convert to annual for tax calculation
        extraWithholding: 0
      });

      console.log('🧮 Tax summary calculated:', taxSummary);

      // Update tax summary in form state for use by PaymentDetailsSection
      setValue('taxSummary', taxSummary);

      // Calculate net pay (gross - taxes - expenses)
      const totalTaxes = Object.values(taxSummary).reduce((sum, val) => sum + (parseFloat(val) || 0), 0);
      const netPayAmount = parseFloat(grossPay) - totalTaxes - monthlyExpenses;

      console.log('💵 Net pay calculation:', { 
        grossPay: parseFloat(grossPay),
        totalTaxes,
        monthlyExpenses,
        netPayAmount 
      });

      // Update net pay field
      setValue('netPay', netPayAmount.toFixed(2));
    }
  }, [grossPay, frequency, state, ...businessExpenses, setValue, watch]);

  // Form validation
  useEffect(() => {
    register('name', { required: 'Income name is required' });
    register('startDate', { required: 'Start date is required' });
    register('state', { required: 'State is required' });
    register('grossPay', { 
      required: 'Gross pay is required',
      validate: value => {
        const numValue = parseFloat(value);
        return (!isNaN(numValue) && numValue > 0) || 'Please enter an amount greater than 0';
      }
    });
    register('frequency', { required: 'Pay frequency is required' });
    register('filingStatus', { required: 'Filing status is required' });
    register('businessStructure', { required: 'Business structure is required' });
    register('isActive');
    register('endDate', { 
      validate: value => {
        if (!value) return true;
        const startDate = watch('startDate');
        if (startDate && new Date(value) <= new Date(startDate)) {
          return 'End date must be after start date';
        }
        return true;
      }
    });
  }, [register, watch]);

  const onSubmit = async (data) => {
    try {
      console.log('🚀 ContractorForm - Form submission started');
      console.log('📋 Raw form data:', data);
      
      // Add validation to ensure required fields are present
      if (!data.name || !data.startDate || !data.state || !data.grossPay || !data.frequency || !data.filingStatus || !data.businessStructure) {
        console.error('❌ Missing required fields:', { 
          name: !!data.name, 
          startDate: !!data.startDate, 
          state: !!data.state, 
          grossPay: !!data.grossPay, 
          frequency: !!data.frequency,
          filingStatus: !!data.filingStatus,
          businessStructure: !!data.businessStructure
        });
        return;
      }
      
      if (parseFloat(data.grossPay) <= 0) {
        console.error('❌ Gross pay must be greater than 0');
        return;
      }
      
      console.log('✅ Form validation passed');
      
      // Parse numeric values
      const parseNumericField = (value) => {
        const parsed = parseFloat(value);
        return isNaN(parsed) ? 0 : parsed;
      };

      // Calculate business expenses
      const businessExpenses = {
        homeOfficeSqft: parseNumericField(data.homeOfficeSqft),
        totalHomeSqft: parseNumericField(data.totalHomeSqft),
        monthlyHomeExpenses: parseNumericField(data.monthlyHomeExpenses),
        equipmentExpenses: parseNumericField(data.equipmentExpenses),
        professionalServices: parseNumericField(data.professionalServices),
        travelExpenses: parseNumericField(data.travelExpenses),
        marketingExpenses: parseNumericField(data.marketingExpenses),
        healthInsurance: parseNumericField(data.healthInsurance),
        otherExpenses: parseNumericField(data.otherExpenses),
        retirementContribution: parseNumericField(data.retirementContribution),
        hsaContribution: parseNumericField(data.hsaContribution)
      };

      // Calculate home office deduction
      let homeOfficeDeduction = 0;
      if (businessExpenses.homeOfficeSqft > 0 && businessExpenses.totalHomeSqft > 0) {
        const percentage = (businessExpenses.homeOfficeSqft / businessExpenses.totalHomeSqft);
        homeOfficeDeduction = businessExpenses.monthlyHomeExpenses * percentage;
      }

      // Helper function to convert expenses to monthly
      const convertToMonthly = (amount) => {
        const numAmount = parseFloat(amount) || 0;
        switch (data.frequency.toLowerCase()) {
          case 'weekly':
            return numAmount * 52 / 12;
          case 'biweekly':
            return numAmount * 26 / 12;
          case 'monthly':
            return numAmount;
          case 'quarterly':
            return numAmount / 3;
          case 'yearly':
            return numAmount / 12;
          default:
            return numAmount;
        }
      };

      // Calculate total monthly business expenses
      const monthlyBusinessExpenses = 
        homeOfficeDeduction +
        convertToMonthly(businessExpenses.equipmentExpenses) +
        convertToMonthly(businessExpenses.professionalServices) +
        convertToMonthly(businessExpenses.travelExpenses) +
        convertToMonthly(businessExpenses.marketingExpenses) +
        convertToMonthly(businessExpenses.healthInsurance) +
        convertToMonthly(businessExpenses.otherExpenses) +
        convertToMonthly(businessExpenses.retirementContribution) +
        convertToMonthly(businessExpenses.hsaContribution);

      // Calculate total business expenses in original frequency for storage
      const totalBusinessExpenses = 
        homeOfficeDeduction +
        businessExpenses.equipmentExpenses +
        businessExpenses.professionalServices +
        businessExpenses.travelExpenses +
        businessExpenses.marketingExpenses +
        businessExpenses.healthInsurance +
        businessExpenses.otherExpenses +
        businessExpenses.retirementContribution +
        businessExpenses.hsaContribution;

      // Calculate tax summary (expects annual deductions)
      const taxSummary = calculateTaxSummary({
        grossPay: parseNumericField(data.grossPay),
        frequency: data.frequency,
        state: data.state,
        filingStatus: data.filingStatus || 'single', // Provide default for calculation
        dependents: 0,
        otherIncome: 0,
        deductions: monthlyBusinessExpenses * 12, // Convert to annual for tax calculation
        extraWithholding: 0
      });

      // Ensure required tax fields are present
      const formattedTaxSummary = {
        ...taxSummary,
        // Convert null or undefined values to 0
        annualGross: taxSummary.annualGross || (parseNumericField(data.grossPay) * 12),
        taxableIncome: taxSummary.taxableIncome || 0,
        federalTax: taxSummary.federalTax || 0,
        stateTax: taxSummary.stateTax || 0,
        fica: taxSummary.fica || (taxSummary.selfEmployment || 0),
        totalTax: taxSummary.totalTax || 0,
        netIncome: taxSummary.netIncome || 0,
        effectiveTaxRate: taxSummary.effectiveTaxRate || 0,
        stateCode: data.state
      };

      // Format the data for API submission
      const formattedData = {
        name: data.name,
        type: 'CONTRACTOR',
        category: 'CONTRACT',
        amount: parseNumericField(data.grossPay),
        netAmount: Math.max(0, parseNumericField(data.netPay)),
        frequency: formatFrequency(data.frequency),
        startDate: data.startDate,
        endDate: data.isActive ? null : data.endDate || null,
        isActive: data.isActive,
        location: {
          state: data.state,
        },
        bucketId: bucketId,
        bucket: bucketId,
        typeSpecificDetails: {
          contractor: {
            employer: data.employer || '',
            filingStatus: data.filingStatus || '',
            businessStructure: data.businessStructure || '',
            state: data.state,
            expenses: {
              ...businessExpenses,
              homeOfficeDeduction,
              totalBusinessExpenses
            },
            taxes: formattedTaxSummary
          }
        }
      };

      console.log('📤 Formatted data for API:', formattedData);

      if (isEditing) {
        console.log('📝 Updating existing income:', { bucketId, incomeId: income._id });
        await updateIncome(bucketId, income._id, formattedData);
        console.log('✅ Income updated successfully');
      } else {
        console.log('➕ Creating new income:', { bucketId });
        await createIncome(bucketId, formattedData);
        console.log('✅ Income created successfully');
      }
      
      console.log('🎉 Form submission completed successfully');
      onSuccess();
    } catch (error) {
      console.error('Error saving contractor income:', error);
    }
  };
  
  return (
    <Form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
      <div className="flex items-center mb-6">
        <button
          type="button"
          onClick={onBack}
          className="inline-flex items-center px-3 py-2 text-sm font-medium text-gray-600 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
        >
          <ArrowLeftIcon className="w-5 h-5 mr-1" />
          Back
        </button>
        <h2 className="ml-4 text-2xl font-semibold text-gray-900">
          {isEditing ? 'Edit Contractor Income' : 'Add Contractor Income'}
        </h2>
      </div>
      
      <BasicInfoSection
        register={register}
        errors={errors}
        watch={watch}
      />

      <PaymentDetailsSection
        register={register}
        errors={errors}
        watch={watch}
        setValue={setValue}
      />
      
      <TaxPlanningSection
        register={register}
        errors={errors}
        watch={watch}
        setValue={setValue}
      />

      <div className="flex justify-end space-x-3">
        <button
          type="button"
          onClick={onBack}
          className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={isSubmitting || Object.keys(errors).length > 0}
          className={`px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md ${
            isSubmitting || Object.keys(errors).length > 0
              ? 'opacity-50 cursor-not-allowed'
              : 'hover:bg-blue-700'
          }`}
        >
          {isEditing ? 'Update Income' : 'Add Income'}
        </button>
      </div>
    </Form>
  );
};

// Helper function to format frequency to match the server model's enum
function formatFrequency(freq) {
  // The server schema requires lowercase frequency values
  const normalizedFreq = (freq || '').toLowerCase();
  
  // Map the frequency to match the schema's enum values
  const frequencyMap = {
    'weekly': 'weekly',
    'biweekly': 'biweekly',
    'monthly': 'monthly',
    'quarterly': 'yearly', // Map quarterly to yearly since it's not in the enum
    'yearly': 'yearly',
    'annual': 'yearly',
    'onetime': 'oneTime'
  };
  
  return frequencyMap[normalizedFreq] || 'monthly'; // Default to monthly if not in map
}

export default ContractorForm; 